import type { AuthenticatedUser } from '@minisoc/contracts';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { api, sessionStore } from '../api';

interface AuthState {
  readonly user: AuthenticatedUser | null;
  readonly accessToken: string | null;
  readonly login: (email: string, password: string) => Promise<void>;
  readonly logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState(() => sessionStore.read());

  // La session peut aussi changer hors de ce composant : rotation du token par
  // le client HTTP, ou expiration détectée sur une requête. L'abonnement évite
  // que l'interface n'affiche un utilisateur déjà déconnecté.
  useEffect(() => sessionStore.subscribe(setSession), []);

  const login = useCallback(async (email: string, password: string) => {
    const response = await api.login(email, password);
    sessionStore.write(response);
  }, []);

  const logout = useCallback(async () => {
    const current = sessionStore.read();
    if (current) {
      // La révocation côté serveur est tentée mais n'est pas bloquante : si elle
      // échoue, la session locale doit tout de même être effacée.
      await api.logout(current.refreshToken).catch(() => undefined);
    }
    sessionStore.clear();
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user: session?.user ?? null,
      accessToken: session?.accessToken ?? null,
      login,
      logout,
    }),
    [session, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth doit être utilisé dans un AuthProvider');
  return context;
}
